/**
 * TransferReportsModal View Component
 *
 * Renders the correct step component based on the current transfer step.
 * The search step stays mounted (hidden via display:none) so input state
 * persists across step transitions. Cancel confirmation renders as the
 * shared ConfirmDialog molecule (SE-5524) overlaying the modal
 * — on native it uses an RN Modal so it stacks above the parent modal,
 * replacing the former inline content-swap approach.
 * @module features/player-detail/components/TransferReportsModal
 */

import { AlertTriangle } from "lucide-react-native";
import { memo } from "react";
import { Platform } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Spinner } from "@/components/atoms/Spinner";
import ConfirmDialog from "@/components/molecules/ConfirmDialog";

import type {
  TransferStep,
  TransferTargetPlayer,
} from "@/features/player-detail/utils/transferReportsTypes";
import TransferReportsConfirmStep from "@/features/player-detail/components/molecules/TransferReportsConfirmStep";
import TransferReportsSearchStep from "@/features/player-detail/components/molecules/TransferReportsSearchStep";
import TransferReportsSummaryStep from "@/features/player-detail/components/molecules/TransferReportsSummaryStep";
import TransferReportsSuccessStep from "@/features/player-detail/components/molecules/TransferReportsSuccessStep";

import BaseModal from "@/components/organisms/shared/BaseModal";

/** Theme key derived from isDark boolean for typed lookups */
type Theme = "dark" | "light";

/** Step titles for the modal header */
const STEP_TITLES: Record<TransferStep, string> = {
  search: "Transfer Reports & Notes",
  summary: "Transfer Summary",
  confirm: "Confirm Transfer",
  transferring: "Transferring...",
  success: "Transfer Complete",
};

/** Pre-computed step visibility to flatten JSX nesting and reduce cognitive complexity */
export interface StepVisibility {
  readonly isSearchVisible: boolean;
  readonly showSummary: boolean;
  readonly showConfirm: boolean;
  readonly showTransferring: boolean;
  readonly showSuccess: boolean;
  readonly theme: Theme;
}

/**
 * Derives which step content is visible from the current step.
 * Computed in the Container so the View stays arrow-shorthand with zero
 * branching. The cancel confirmation no longer hides steps — it overlays
 * them as a ConfirmDialog (SE-5524).
 * @param step - Current transfer step
 * @param isDark - Whether dark mode is active
 */
export const getStepVisibility = (
  step: TransferStep,
  isDark: boolean
): StepVisibility => ({
  isSearchVisible: step === "search",
  showSummary: step === "summary",
  showConfirm: step === "confirm",
  showTransferring: step === "transferring",
  showSuccess: step === "success",
  theme: isDark ? "dark" : "light",
});

/** Props for TransferReportsModalView */
export interface TransferReportsModalViewProps {
  /** Whether the modal is visible */
  readonly isOpen: boolean;
  /** Current step in the flow */
  readonly step: TransferStep;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Source manual player name */
  readonly sourcePlayerName: string;
  /** Source player position abbreviation */
  readonly sourcePositionAbbreviation: string | null;
  /** Source player team name */
  readonly sourceTeamName: string | null;
  /** Scout report count to transfer */
  readonly scoutReportCount: number;
  /** Note count to transfer */
  readonly noteCount: number;
  /** Selected target player (null until search completes) */
  readonly targetPlayer: TransferTargetPlayer | null;
  /** Callback to close the modal */
  readonly onClose: () => void;
  /** Callback when a target player is selected in search */
  readonly onSelectPlayer: (player: TransferTargetPlayer) => void;
  /** Callback when "Transfer All" is pressed in summary */
  readonly onTransferAll: () => void;
  /** Callback when transfer is confirmed in confirm step */
  readonly onConfirm: () => void;
  /** Callback to go back to search from summary */
  readonly onBackToSearch: () => void;
  /** Callback to go back to summary from confirm */
  readonly onBackToSummary: () => void;
  /** Callback when "Done" is pressed on success screen */
  readonly onDone: () => void;
  /** Whether the cancel confirmation dialog is visible */
  readonly showCancelConfirm: boolean;
  /** Callback when user confirms cancellation */
  readonly onCancelConfirm: () => void;
  /** Callback when user dismisses cancellation dialog */
  readonly onCancelDismiss: () => void;
  /** Pre-computed step visibility flags from Container */
  readonly visibility: StepVisibility;
}

/**
 * Renders the appropriate step component based on pre-computed visibility.
 * Search stays mounted for input persistence. Cancel renders inline.
 * Visibility is derived in the Container via getStepVisibility to keep
 * this View free of branching logic.
 * @param root0 - Component props
 * @param root0.isOpen - Whether the modal is visible
 * @param root0.step - Current step
 * @param root0.isDark - Whether dark mode is active
 * @param root0.sourcePlayerName - Source player name
 * @param root0.sourcePositionAbbreviation - Source position abbreviation
 * @param root0.sourceTeamName - Source team name
 * @param root0.scoutReportCount - Reports count
 * @param root0.noteCount - Notes count
 * @param root0.targetPlayer - Selected target player
 * @param root0.onClose - Close handler
 * @param root0.onSelectPlayer - Player selection handler
 * @param root0.onTransferAll - Transfer All handler
 * @param root0.onConfirm - Confirm handler
 * @param root0.onBackToSearch - Back to search handler
 * @param root0.onBackToSummary - Back to summary handler
 * @param root0.onDone - Done handler on success screen
 * @param root0.showCancelConfirm - Whether cancel confirmation is visible
 * @param root0.onCancelConfirm - Cancel confirmation handler
 * @param root0.onCancelDismiss - Cancel dismissal handler
 * @param root0.visibility - Pre-computed step visibility flags
 * @param root0.visibility.isSearchVisible - Whether search step is visible
 * @param root0.visibility.showSummary - Whether summary step is visible
 * @param root0.visibility.showConfirm - Whether confirm step is visible
 * @param root0.visibility.showTransferring - Whether transferring step is visible
 * @param root0.visibility.showSuccess - Whether success step is visible
 * @param root0.visibility.theme - Resolved theme key
 */
const TransferReportsModalView = ({
  isOpen,
  step,
  isDark,
  sourcePlayerName,
  sourcePositionAbbreviation,
  sourceTeamName,
  scoutReportCount,
  noteCount,
  targetPlayer,
  onClose,
  onSelectPlayer,
  onTransferAll,
  onConfirm,
  onBackToSearch,
  onBackToSummary,
  onDone,
  showCancelConfirm,
  onCancelConfirm,
  onCancelDismiss,
  visibility: {
    isSearchVisible,
    showSummary,
    showConfirm,
    showTransferring,
    showSuccess,
    theme,
  },
}: TransferReportsModalViewProps) => (
  <BaseModal
    isOpen={isOpen}
    onClose={onClose}
    title={STEP_TITLES[step]}
    size="lg"
    minHeight="80vh"
    maxHeight="80vh"
    closeOnOverlayClick={false}
  >
    {/* Search always mounted so input state persists across step changes */}
    <Box className={isSearchVisible ? "flex-1" : "hidden"}>
      <TransferReportsSearchStep
        isDark={isDark}
        onSelectPlayer={onSelectPlayer}
      />
    </Box>

    {showSummary && targetPlayer ? (
      <TransferReportsSummaryStep
        sourcePlayerName={sourcePlayerName}
        sourcePositionAbbreviation={sourcePositionAbbreviation}
        sourceTeamName={sourceTeamName}
        targetDisplayName={targetPlayer.displayName}
        targetPositionAbbreviation={targetPlayer.positionAbbreviation}
        targetTeamName={targetPlayer.teamName}
        scoutReportCount={scoutReportCount}
        noteCount={noteCount}
        theme={theme}
        onTransferAll={onTransferAll}
        onBack={onBackToSearch}
      />
    ) : null}

    {showConfirm && targetPlayer ? (
      <TransferReportsConfirmStep
        sourcePlayerName={sourcePlayerName}
        targetPlayerName={targetPlayer.displayName}
        totalItemCount={scoutReportCount + noteCount}
        onConfirm={onConfirm}
        onBack={onBackToSummary}
      />
    ) : null}

    {showTransferring ? (
      <Box
        className="flex-1 items-center justify-center"
        testID="transfer-loading"
      >
        <Spinner size="large" color="#F59E0B" />
      </Box>
    ) : null}

    {showSuccess ? (
      <TransferReportsSuccessStep isDark={isDark} onDone={onDone} />
    ) : null}

    <ConfirmDialog
      isOpen={showCancelConfirm}
      onClose={onCancelDismiss}
      onConfirm={onCancelConfirm}
      title="Cancel Transfer?"
      body="Your progress will be lost. You can start a new transfer at any time."
      icon={AlertTriangle}
      tone="warning"
      cancelLabel="Continue Transfer"
      confirmLabel="Yes, Cancel"
      cancelTestID="cancel-transfer-dismiss"
      confirmTestID="cancel-transfer-confirm"
      // Capability gating (not layout): native RN Modal stacking so the
      // dialog renders above the parent transfer modal on iOS/Android.
      useRNModal={Platform.OS !== "web"}
    />
  </BaseModal>
);

TransferReportsModalView.displayName = "TransferReportsModalView";

export default memo(TransferReportsModalView);
